<script lang="ts">
    import { fade, scale } from 'svelte/transition';

    interface Props {
        open: boolean;
        title?: string;
        message?: string;
        confirmLabel?: string;
        cancelLabel?: string;
        variant?: 'danger' | 'warning' | 'success';
        panelClass?: string;
        onconfirm: () => void;
        oncancel: () => void;
    }

    let {
        open = $bindable(false),
        title = 'Are you sure?',
        message = '',
        confirmLabel = 'Confirm',
        cancelLabel = 'Cancel',
        variant = 'danger',
        panelClass = '',
        onconfirm,
        oncancel,
    }: Props = $props();

    function handleConfirm() {
        open = false;
        onconfirm();
    }

    function handleCancel() {
        open = false;
        oncancel?.();
    }

    function handleBackdrop(e: MouseEvent) {
        if (e.target === e.currentTarget) {
            handleCancel();
        }
    }

    function handleKeydown(e: KeyboardEvent) {
        if (e.key === 'Escape') {
            handleCancel();
        }
    }

    const confirmClasses: Record<string, string> = {
        danger: 'bg-red-600 hover:bg-red-700 focus-visible:outline-red-600',
        warning: 'bg-amber-600 hover:bg-amber-700 focus-visible:outline-amber-600',
        success: 'bg-green-600 hover:bg-green-700 focus-visible:outline-green-600',
    };

</script>

<svelte:window onkeydown={open ? handleKeydown : undefined} />

{#if open}
    <div
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        onclick={handleBackdrop}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        transition:fade={{ duration: 150 }}
    >
        <div class="bg-gray-900 rounded-lg w-full max-w-md mx-4 p-6 {panelClass || 'shadow-xl'}" transition:scale={{ duration: 200, start: 0.95 }}>
            <h3 id="confirm-modal-title" class="text-lg font-semibold text-gray-100">{title}</h3>
            {#if message}
                <p class="mt-2 text-sm text-gray-400">{message}</p>
            {/if}
            <div class="mt-6 flex justify-end gap-3">
                <button
                    onclick={handleCancel}
                    class="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-md hover:bg-gray-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400"
                >
                    {cancelLabel}
                </button>
                <button
                    onclick={handleConfirm}
                    class="px-4 py-2 text-sm font-medium text-white rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 {confirmClasses[variant]}"
                >
                    {confirmLabel}
                </button>
            </div>
        </div>
    </div>
{/if}
